import { laborManagementApi } from '../laborManagementApi';
import { StaffingRuleView, DepartmentNameView } from '../types/laborManagementTypes';
import {
  FacilityQuery,
  StaffingRuleChangeRequest,
  StaffingRuleUpsertRequest,
} from '../requests/laborManagementRequests';

// A successful scoped save re-reads the list, so the grid redraws on the identifiers the server
// minted. A refused save invalidates nothing: the refetch would swap the values out from under the
// staleness message while the administrator is reading it. Lives outside the endpoint builder so
// the rule is testable on its own, as `upsertProjectionTags` is.
export const saveStaffingRuleChangeTags = (error: unknown, { id }: StaffingRuleChangeRequest) =>
  error
    ? []
    : [
        { type: 'StaffingRule' as const, id },
        { type: 'StaffingRule' as const, id: 'LIST' },
      ];

const staffingEndpointsInjected = laborManagementApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /DepartmentNames
    getDepartmentNames: builder.query<DepartmentNameView[], void>({
      query: () => 'DepartmentNames',
      keepUnusedDataFor: 3600, // Cache for 1 hour - this is static data
    }),

    // GET /StaffingRules?company=&centerId=
    getStaffingRulesByFacility: builder.query<StaffingRuleView[], FacilityQuery>({
      query: ({ company, centerId }) => ({
        url: 'StaffingRules',
        params: { company, centerId },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'StaffingRule' as const, id })),
              { type: 'StaffingRule', id: 'LIST' },
            ]
          : [{ type: 'StaffingRule', id: 'LIST' }],
      keepUnusedDataFor: 120,
    }),

    // GET /StaffingRule?request=<id>
    getStaffingRuleById: builder.query<StaffingRuleView, string>({
      query: (id) => ({
        url: 'StaffingRule',
        params: { request: id },
      }),
      providesTags: (result) =>
        result ? [{ type: 'StaffingRule', id: result.id }] : [],
      keepUnusedDataFor: 120,
    }),

    // POST /StaffingRule
    upsertStaffingRule: builder.mutation<StaffingRuleView, StaffingRuleUpsertRequest>({
      query: (body) => ({
        url: 'StaffingRule',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'StaffingRule', id: result.id },
              { type: 'StaffingRule', id: 'LIST' },
            ]
          : [{ type: 'StaffingRule', id: 'LIST' }],
    }),

    // POST /StaffingRule/Change — applies only the parts the body carries
    saveStaffingRuleChange: builder.mutation<StaffingRuleView, StaffingRuleChangeRequest>({
      query: (body) => ({
        url: 'StaffingRule/Change',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error, arg) => saveStaffingRuleChangeTags(error, arg),
    }),
  }),
});

export const {
  useGetDepartmentNamesQuery,
  useGetStaffingRulesByFacilityQuery,
  useGetStaffingRuleByIdQuery,
  useUpsertStaffingRuleMutation,
  useSaveStaffingRuleChangeMutation,
} = staffingEndpointsInjected;
